import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import USPTOApi from '../../../api/data-sections/usptoApi';
import StandardDataGrid from '../../../components/Common/StandardDataGrid';
import ApplicationModal from '../../../components/DataSections/USPTO/Modal/ApplicationModal';
import AssignmentModal from '../../../components/DataSections/USPTO/Modal/AssignmentModal';
import GrantModal from '../../../components/DataSections/USPTO/Modal/GrantModal';
import { useSystemError } from '../../../components/Error/SystemErrorContext';
import { UsptoApplicationModel } from '../../../models/data-sections/uspto/applicationModel';
import { UsptoAssignmentModel } from '../../../models/data-sections/uspto/assignmentModel';
import { UsptoGrantModel } from '../../../models/data-sections/uspto/grantModel';
import { ResponseErrorModel } from '../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../models/system/responseSuccessModel';
import { SearchesModel } from '../../../models/platform/searchesModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../utils/errorUtils';


interface USPTOProps {
    searchExecutionId: string;
    applications: UsptoApplicationModel[] | null;
    grants: UsptoGrantModel[] | null;
    assignments: UsptoAssignmentModel[] | null;
    isLoading: boolean;
}

const USPTO: React.FC<USPTOProps> = ({ searchExecutionId, applications, grants, assignments, isLoading }) => {
    const { triggerSystemError } = useSystemError();

    const applicationColumns: GridColDef<UsptoApplicationModel>[] = [
        { field: 'application_country', headerName: 'Country', width: 120 },
        { field: 'application_date', headerName: 'Date', width: 120 },
        { field: 'application_doc_number', headerName: 'Doc Number', width: 150 },
        {
            field: 'applicants',
            headerName: 'Applicants',
            width: 300,
            renderCell: (params) => {
                const applicantList = Array.isArray(params.value)
                    ? params.value.map((a) => a.orgname).filter(Boolean).join(', ')
                    : '';
                return <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{applicantList || 'N/A'}</div>;
            },
        },
        {
            field: 'invention_title',
            headerName: 'Invention Title',
            flex: 1,
            renderCell: (params) => <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{params.value}</div>,
        },
    ];

    const grantColumns: GridColDef<UsptoGrantModel>[] = [
        { field: 'publication_country', headerName: 'Country', width: 120 },
        { field: 'publication_doc_number', headerName: 'Doc Number', width: 150 },
        {
            field: 'applicants',
            headerName: 'Applicants',
            width: 300,
            renderCell: (params) => {
                const applicantList = Array.isArray(params.value)
                    ? params.value.map((a) => a.orgname).filter(Boolean).join(', ')
                    : '';
                return <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{applicantList || 'N/A'}</div>;
            },
        },
        {
            field: 'invention_title',
            headerName: 'Invention Title',
            flex: 1,
            renderCell: (params) => <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{params.value}</div>,
        },
    ];

    const assignmentColumns: GridColDef<UsptoAssignmentModel>[] = [
        { field: 'recorded_date', headerName: 'Recorded Date', width: 150 },
        {
            field: 'assignors',
            headerName: 'Assignors',
            width: 300,
            renderCell: (params) => {
                const names = Array.isArray(params.value)
                    ? params.value.map(a => a.name).filter(Boolean).join(', ')
                    : '';
                return <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{names || 'N/A'}</div>;
            },
        },
        {
            field: 'assignees',
            headerName: 'Assignees',
            width: 300,
            renderCell: (params) => {
                const names = Array.isArray(params.value)
                    ? params.value.map(a => a.name).filter(Boolean).join(', ')
                    : '';
                return <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{names || 'N/A'}</div>;
            },
        },
        { field: 'reel_no', headerName: 'Reel No.', width: 150 },
        {
            field: 'invention_title',
            headerName: 'Invention Title',
            flex: 1,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{params.value || 'N/A'}</div>
            ),
        },
    ];

    const tabs = [
        {
            label: 'Applications',
            rows: applications?.map((a, i) => ({ id: i, ...a })) || [],
            columns: applicationColumns,
            modalRenderer: (row: UsptoApplicationModel) => (
                <ApplicationModal
                    searchExecutionId={searchExecutionId}
                    selected_application={row}
                />
            ),
        },
        {
            label: 'Grants',
            rows: grants?.map((g, i) => ({ id: i, ...g })) || [],
            columns: grantColumns,
            modalRenderer: (row: UsptoGrantModel) => (
                <GrantModal
                    searchExecutionId={searchExecutionId}
                    selected_grant={row}
                />
            ),
        },
        {
            label: 'Assignments',
            rows: assignments?.map((g, i) => ({ id: i, ...g })) || [],
            columns: assignmentColumns,
            modalRenderer: (row: UsptoAssignmentModel) => (
                <AssignmentModal
                    searchExecutionId={searchExecutionId}
                    selected_assignment={row}
                />
            ),
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 150px)', width: '100%' }}>
            <StandardDataGrid isLoading={isLoading} tabs={tabs} />
        </Box>
    );
};

export default USPTO;
